// ── BCH Live — Stream Liveness ────────────────────────────────────
//
// Extracted verbatim (behaviour-wise) from the original viewer page so
// the hard-won reliability work is preserved while the page itself
// becomes a thin composition layer.
//
// Three pieces:
//   • A persistent relay pool, so status polling doesn't pay WebSocket
//     setup cost every cycle.
//   • useStreamStatus  — picks the freshest kind 30311 for a creator and
//     derives liveness purely from heartbeat age (no state machine).
//   • useConnectionHealth / useResolvePubkey — supporting concerns.
//
// WHY NO STATE MACHINE: liveness tolerance lives in exactly one place —
// `isLive` is false only once the newest heartbeat is older than
// HEARTBEAT_TIMEOUT_SECS. Because `directEvents` retains the last event
// we saw, a relay that briefly returns nothing cannot change the answer.
// Time-based tolerance, for free, with no counters to get out of sync.

import { useState, useEffect, useMemo, useRef } from 'react';
import type { Event } from 'nostr-tools';
import { useNostrQuery } from './useNostrQuery';
import {
  STREAM_EVENT_KIND, HEARTBEAT_TIMEOUT_SECS,
  parseStreamEvent, detectPlatform, slog, swarn, describeStream,
  type StreamMeta,
} from '../lib/streamCredentials';

// ══════════════════════════════════════════════════════════════════
//  PERSISTENT RELAY POOL
// ══════════════════════════════════════════════════════════════════

export const STREAM_RELAYS = [
  'wss://relay.bchnostr.com',
  'wss://nos.lol', 'wss://relay.primal.net',
  'wss://relay.nostr.band', 'wss://relay.snort.social',
  'wss://purplepag.es', 'wss://nostr.wine',
];

const streamRelayPool = new Map<string, WebSocket>();
const streamRelayFails = new Map<string, number>();
const MAX_STREAM_RELAY_FAILS = 8;

function getOrConnectStreamRelay(url: string): WebSocket | null {
  const existing = streamRelayPool.get(url);
  if (existing && existing.readyState <= 1) return existing;

  const fails = streamRelayFails.get(url) || 0;
  if (fails >= MAX_STREAM_RELAY_FAILS) return null;

  try {
    const ws = new WebSocket(url);
    streamRelayPool.set(url, ws);

    ws.onopen = () => {
      streamRelayFails.set(url, 0);
      slog('relay', `connected ${url}`);
    };
    ws.onclose = () => {
      streamRelayPool.delete(url);
      const f = (streamRelayFails.get(url) || 0) + 1;
      streamRelayFails.set(url, f);
      if (f < MAX_STREAM_RELAY_FAILS) {
        const delay = Math.min(2000 * Math.pow(1.5, f), 30000);
        swarn('relay', `disconnected ${url} (fail #${f}) — reconnecting in ${Math.round(delay)}ms`);
        setTimeout(() => {
          if (!streamRelayPool.has(url)) getOrConnectStreamRelay(url);
        }, delay);
      } else {
        swarn('relay', `giving up on ${url} after ${f} failures`);
      }
    };
    ws.onerror = () => { /* onclose handles the bookkeeping */ };
    return ws;
  } catch {
    return null;
  }
}

/** Open the pool early so the first poll doesn't wait on TCP + TLS. */
export function preconnectStreamRelays(): void {
  STREAM_RELAYS.forEach(getOrConnectStreamRelay);
}

/**
 * Newest liveness signal a kind 30311 event carries.
 *
 * Ranking by `created_at` alone is wrong: relays disagree about which
 * version of a replaceable event they hold, and some clients bump
 * `heartbeat` without bumping `created_at`, so a fresher event could
 * lose to a staler one.
 *
 * CRITICAL: `starts` is clamped to now. For a SCHEDULED stream `starts` is
 * a FUTURE timestamp, and counting it raw made a stream planned for
 * tomorrow score higher than one that is live right now — so the viewer
 * would show the scheduled event instead of the live broadcast. Only past
 * timestamps say anything about freshness; a future one says nothing.
 */
function streamFreshness(ev: Event): number {
  const now = Math.floor(Date.now() / 1000);
  const tag = (n: string) => ev.tags?.find(t => t[0] === n)?.[1];
  const starts = parseInt(tag('starts') || tag('started_at') || '0', 10) || 0;
  return Math.max(
    ev.created_at || 0,
    parseInt(tag('heartbeat') || '0', 10) || 0,
    starts > now ? 0 : starts,
  );
}

/**
 * Selection priority for competing kind 30311 events from one creator.
 *
 * A creator legitimately has several: the live slot (`d` == pubkey) plus a
 * `d` per scheduled broadcast. They must NOT be ranked on timestamps
 * alone — a live stream's heartbeat advances every 30s while a scheduled
 * event's `starts` sits fixed, so whichever happened to be larger flipped
 * as the clock moved and the player swapped between them mid-watch.
 *
 * Liveness is therefore the primary key and always wins:
 *   2 = live right now   1 = upcoming   0 = ended / unknown
 */
function streamPriority(ev: Event): number {
  const tag = (n: string) => ev.tags?.find(t => t[0] === n)?.[1];
  const status = tag('status');
  if (status === 'live') {
    // Trust `live` only while the heartbeat is fresh, mirroring
    // parseStreamEvent so ranking and rendering can't disagree.
    const now = Math.floor(Date.now() / 1000);
    const beat = Math.max(
      ev.created_at || 0,
      parseInt(tag('heartbeat') || '0', 10) || 0,
    );
    if (now - beat <= HEARTBEAT_TIMEOUT_SECS) return 2;
    return 0; // claims live but has gone quiet — rank with the dead
  }
  if (status === 'planned') {
    const starts = parseInt(tag('starts') || '0', 10) || 0;
    // A planned stream whose time has passed without going live is stale.
    return starts > Math.floor(Date.now() / 1000) ? 1 : 0;
  }
  return 0;
}

// ══════════════════════════════════════════════════════════════════
//  DIRECT RELAY FETCH
//
//  Bypasses the useNostrQuery cache entirely and queries every connected
//  relay for redundancy.
// ══════════════════════════════════════════════════════════════════

function fetchStreamEventsDirect(pubkey: string): Promise<Event[]> {
  const since = Math.floor(Date.now() / 1000) - 86400 * 2;

  return new Promise((resolve) => {
    const events: Event[] = [];
    const eventIds = new Set<string>();
    let resolved = false;
    let remaining = 0;
    let eoseCount = 0;

    preconnectStreamRelays();

    const activeRelays: string[] = [];
    STREAM_RELAYS.forEach(url => {
      const ws = streamRelayPool.get(url);
      if (ws && ws.readyState === WebSocket.OPEN) activeRelays.push(url);
    });

    const startedAt = Date.now();
    const done = () => {
      if (resolved) return;
      resolved = true;
      slog('poll', `finished in ${Date.now() - startedAt}ms — ${events.length} event(s) from ${eoseCount}/${activeRelays.length} relay(s)`);
      resolve(events);
    };

    // Safety net — resolve with whatever we have after 8s.
    setTimeout(() => {
      if (!resolved) swarn('poll', `global 8s timeout — resolving with ${events.length} event(s)`);
      done();
    }, 8000);

    if (activeRelays.length === 0) {
      swarn('poll', 'NO relays connected — cannot check stream status this cycle');
      preconnectStreamRelays();
      resolved = true;
      resolve([]);
      return;
    }

    slog('poll', `querying ${activeRelays.length}/${STREAM_RELAYS.length} connected relay(s) for ${pubkey.slice(0, 8)}`);
    remaining = activeRelays.length;

    activeRelays.forEach(url => {
      const ws = streamRelayPool.get(url);
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        remaining--;
        if (remaining <= 0) done();
        return;
      }

      const subId = 'ss_' + Math.random().toString(36).slice(2, 8);

      const handler = (msg: MessageEvent) => {
        try {
          const data = JSON.parse(msg.data);
          if (data[0] === 'EVENT' && data[1] === subId && data[2]) {
            const ev = data[2] as Event;
            if (!Array.isArray(ev.tags)) ev.tags = [];
            if (typeof ev.content !== 'string') ev.content = '';

            // Per-relay visibility: shows WHICH relays serve stale versions
            // of this replaceable event vs which have the fresh one.
            const hb = parseInt(ev.tags.find(t => t[0] === 'heartbeat')?.[1] || '0', 10);
            const st = ev.tags.find(t => t[0] === 'status')?.[1] || '?';
            const beatAge = Math.floor(Date.now() / 1000) - Math.max(hb || 0, ev.created_at);
            slog('poll', `  ${url} → status=${st} beat=${beatAge}s ago`);

            if (ev.id && !eventIds.has(ev.id)) {
              eventIds.add(ev.id);
              events.push(ev);
            }
          }
          if (data[0] === 'EOSE' && data[1] === subId) {
            try { ws.send(JSON.stringify(['CLOSE', subId])); } catch { /* closing */ }
            ws.removeEventListener('message', handler);
            eoseCount++;
            remaining--;
            // Do NOT resolve on the first relay to answer. Kind 30311 is
            // replaceable, so relays hold DIFFERENT versions of the same
            // stream; taking the fastest relay's copy meant routinely
            // reading a stale heartbeat and declaring a live creator dead.
            if (remaining <= 0) done();
          }
        } catch { /* malformed relay frame */ }
      };

      ws.addEventListener('message', handler);

      try {
        ws.send(JSON.stringify(['REQ', subId, {
          kinds: [STREAM_EVENT_KIND],
          authors: [pubkey],
          limit: 5,
          since,
        }]));
      } catch {
        ws.removeEventListener('message', handler);
        remaining--;
        if (remaining <= 0) done();
      }

      // Per-relay timeout — one slow relay can't hold up the batch.
      setTimeout(() => {
        ws.removeEventListener('message', handler);
        try { ws.send(JSON.stringify(['CLOSE', subId])); } catch { /* closing */ }
        remaining--;
        if (remaining <= 0) done();
      }, 6000);
    });
  });
}

// ══════════════════════════════════════════════════════════════════
//  useStreamStatus
// ══════════════════════════════════════════════════════════════════

export interface UseStreamStatusResult {
  stream: StreamMeta | null;
  loading: boolean;
  /** Tags of the winning event, so callers can read category/topics */
  tags: string[][];
}

export function useStreamStatus(pubkey: string | null): UseStreamStatusResult {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const filters = useMemo(
    () => (pubkey ? [{ kinds: [STREAM_EVENT_KIND], authors: [pubkey], limit: 20, since }] : []),
    [pubkey, since],
  );
  const { events, loading: rawLoading } = useNostrQuery({ filters, allRelays: true });

  // Report "loading" only until the first result arrives. useNostrQuery flips
  // loading=true on every refetch, and passing that through would flip the
  // page's spinner ternary mid-stream and remount the player subtree.
  const hasLoadedRef = useRef(false);
  if (!rawLoading) hasLoadedRef.current = true;
  const loading = !hasLoadedRef.current && rawLoading;

  const [directEvents, setDirectEvents] = useState<Event[]>([]);
  const lastSuccessfulPollRef = useRef(Date.now());
  const lastSeenBeatRef = useRef(0);

  // `isLive` is a function of Date.now(), so without a tick a stream that
  // stopped beating would stay "live" on screen forever. This is the ONLY
  // thing driving the live→offline transition.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (!pubkey) return;
    const i = setInterval(() => setNowTick(t => t + 1), 15_000);
    return () => clearInterval(i);
  }, [pubkey]);

  useEffect(() => {
    if (pubkey) preconnectStreamRelays();
  }, [pubkey]);

  // ── Direct poll every 20s ──
  useEffect(() => {
    if (!pubkey) return;
    let mounted = true;
    let consecutiveEmpty = 0;

    const poll = async () => {
      try {
        const fresh = await fetchStreamEventsDirect(pubkey);
        if (!mounted) return;

        if (fresh.length > 0) {
          if (consecutiveEmpty > 0) slog('poll', `relays responding again after ${consecutiveEmpty} empty cycle(s)`);
          consecutiveEmpty = 0;
          lastSuccessfulPollRef.current = Date.now();

          // Remote heartbeat tracking. The creator's own `beat` logs only
          // appear in their browser, so from the outside this is the only
          // window into whether their heartbeat is still arriving.
          const nowSec = Math.floor(Date.now() / 1000);
          let newestBeat = 0;
          for (const ev of fresh) newestBeat = Math.max(newestBeat, streamFreshness(ev));
          if (newestBeat > 0) {
            const age = nowSec - newestBeat;
            const prevBeat = lastSeenBeatRef.current;
            if (newestBeat > prevBeat) {
              const gap = prevBeat ? ` (+${newestBeat - prevBeat}s since previous)` : '';
              slog('remote', `creator heartbeat is FRESH — ${age}s old${gap}`);
              lastSeenBeatRef.current = newestBeat;
            } else if (age > HEARTBEAT_TIMEOUT_SECS) {
              swarn('remote', `creator heartbeat is STALE — ${age}s old, past the ${HEARTBEAT_TIMEOUT_SECS}s limit. Their publisher has stopped.`);
            } else {
              // Common and benign: our 20s poll is faster than their 30s beat.
              slog('remote', `no new heartbeat this cycle — newest ${age}s old, still within the ${HEARTBEAT_TIMEOUT_SECS}s limit`);
            }
          }

          setDirectEvents(prev => {
            const byId = new Map<string, Event>();
            for (const ev of prev) byId.set(ev.id, ev);
            for (const ev of fresh) {
              const existing = byId.get(ev.id);
              if (!existing || ev.created_at > existing.created_at) byId.set(ev.id, ev);
            }
            return Array.from(byId.values());
          });
        } else {
          consecutiveEmpty++;
          const quietFor = Math.round((Date.now() - lastSuccessfulPollRef.current) / 1000);
          swarn('poll', `empty result #${consecutiveEmpty} — no 30311 events for this creator (${quietFor}s since last data)`);
          if (consecutiveEmpty >= 3) {
            swarn('poll', 'forcing full relay reconnect after 3 empty cycles');
            STREAM_RELAYS.forEach(url => {
              streamRelayFails.set(url, 0);
              getOrConnectStreamRelay(url);
            });
          }
        }
      } catch (err) {
        swarn('poll', 'poll threw', err);
      }
    };

    // Let the initial cached query settle before the first direct poll.
    const initial = setTimeout(poll, 3000);
    const interval = setInterval(poll, 20_000);

    // Browsers throttle timers in background tabs, so catch up on return.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && mounted) {
        poll();
        STREAM_RELAYS.forEach(url => {
          const ws = streamRelayPool.get(url);
          if (!ws || ws.readyState > 1) {
            streamRelayFails.set(url, 0);
            getOrConnectStreamRelay(url);
          }
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mounted = false;
      clearTimeout(initial);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pubkey]);

  // Kind 30311 is replaceable: collapse by coordinate (pubkey + d) and keep
  // only the freshest version. Deduping by event id instead let every
  // relay's stale copy pile up, where a stale one could win.
  const mergedEvents = useMemo(() => {
    const byCoord = new Map<string, Event>();
    for (const ev of [...events, ...directEvents]) {
      if (!ev.id) continue;
      const d = ev.tags?.find(t => t[0] === 'd')?.[1] || '';
      const coord = `${ev.pubkey}:${d}`;
      const existing = byCoord.get(coord);
      if (!existing || streamFreshness(ev) > streamFreshness(existing)) byCoord.set(coord, ev);
    }
    return Array.from(byCoord.values());
  }, [events, directEvents]);

  // The winning event's `d` coordinate. Once we lock onto a creator's live
  // broadcast we keep showing THAT coordinate, so a scheduled event can
  // never steal the player mid-watch.
  const lockedCoordRef = useRef<string | null>(null);

  // The `streaming` URL we first showed for the locked coordinate. Latching
  // this is what actually stops the player from swapping to "another video"
  // mid-watch: kind 30311 is replaceable, and different relays hold different
  // versions of the SAME coordinate. When their `streaming` tags disagree —
  // a rotated HLS token URL, a re-published event, or simply an older version
  // winning a freshness tie — the URL handed to the player flips, which tears
  // down the HLS engine or reloads the iframe. Coordinate stickiness alone
  // does not prevent that, because the churn is WITHIN one coordinate.
  //
  // So: while we stay locked to a live coordinate, keep serving the first URL
  // we committed to. We only adopt a new URL when the coordinate itself
  // changes (a genuinely different broadcast) or the lock is released.
  const latchedUrlRef = useRef<{ coord: string; url: string } | null>(null);

  const { stream, tags } = useMemo(() => {
    if (!pubkey || mergedEvents.length === 0) return { stream: null, tags: [] as string[][] };

    const coordOf = (ev: Event) =>
      `${ev.pubkey}:${ev.tags?.find(t => t[0] === 'd')?.[1] || ''}`;

    // Liveness first, then freshness. Ranking on timestamps alone let a
    // scheduled broadcast outrank a live one (see streamPriority).
    const ranked = [...mergedEvents].sort((a, b) =>
      (streamPriority(b) - streamPriority(a)) ||
      (streamFreshness(b) - streamFreshness(a)));

    let best = ranked[0];
    if (!best) return { stream: null, tags: [] as string[][] };

    // ── Stickiness ──
    // If we're already locked onto a coordinate that is STILL live, stay on
    // it. Without this, two simultaneously-live events (e.g. a creator who
    // re-published under a new `d`) would swap on every heartbeat tick.
    const locked = lockedCoordRef.current;
    if (locked) {
      const lockedEvent = ranked.find(ev => coordOf(ev) === locked);
      if (!lockedEvent) {
        // The coordinate vanished from the relay results. Release the lock,
        // otherwise it would pin us to a coordinate we can never see again.
        slog('select', `locked coordinate ${locked} disappeared — releasing lock`);
        lockedCoordRef.current = null;
        latchedUrlRef.current = null;
      } else if (streamPriority(lockedEvent) === 2) {
        if (coordOf(best) !== locked) {
          slog('select', `staying on locked coordinate ${locked} (still live) instead of switching`);
        }
        best = lockedEvent;
      } else {
        // Locked stream is no longer live — free to follow the ranking.
        slog('select', `locked coordinate ${locked} is no longer live — releasing lock`);
        lockedCoordRef.current = null;
        latchedUrlRef.current = null;
      }
    }

    // Lock on once a live broadcast is found.
    if (streamPriority(best) === 2 && lockedCoordRef.current !== coordOf(best)) {
      lockedCoordRef.current = coordOf(best);
      slog('select', `locked onto live coordinate ${coordOf(best)}`);
    }

    if (ranked.length > 1) {
      slog('select', `chose ${coordOf(best)} from ${ranked.length} candidate(s): priority=${streamPriority(best)} beat=${Math.floor(Date.now() / 1000) - streamFreshness(best)}s ago`);
    }

    const parsed = parseStreamEvent(best);

    // ── URL latch ──
    // While a live coordinate stays locked, pin the `streaming` URL to the
    // first one we committed to. This is the mechanism that stops the player
    // from switching to "another video" mid-stream when relays serve
    // conflicting versions of the same replaceable event.
    const bestCoord = coordOf(best);
    if (parsed.isLive && lockedCoordRef.current === bestCoord && parsed.streamingUrl) {
      const latch = latchedUrlRef.current;
      if (latch && latch.coord === bestCoord) {
        if (latch.url !== parsed.streamingUrl) {
          swarn('select', `ignoring streaming-URL change on locked coordinate ${bestCoord} — keeping the original source to avoid a mid-stream swap. latched=${latch.url.slice(0, 60)} incoming=${parsed.streamingUrl.slice(0, 60)}`);
          parsed.streamingUrl = latch.url;
          parsed.platformInfo = detectPlatform(latch.url);
        }
      } else {
        latchedUrlRef.current = { coord: bestCoord, url: parsed.streamingUrl };
        slog('select', `latched streaming URL for ${bestCoord} → ${parsed.streamingUrl.slice(0, 60)}`);
      }
    }

    return { stream: parsed, tags: best.tags || [] };
    // nowTick is a deliberate dependency: it re-derives liveness on a clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedEvents, pubkey, nowTick]);

  // Drop the lock (and the URL latch) when the creator changes, or they would
  // pin the new creator's page to the previous creator's coordinate and URL.
  useEffect(() => {
    lockedCoordRef.current = null;
    latchedUrlRef.current = null;
  }, [pubkey]);

  // Log liveness transitions only, so the console tells the story once.
  const prevLiveRef = useRef<boolean | null>(null);
  useEffect(() => {
    const live = stream?.isLive ?? false;
    if (prevLiveRef.current === live) return;
    prevLiveRef.current = live;
    if (live) slog('liveness', `LIVE → ${describeStream(stream)}`);
    else if (stream) slog('liveness', `OFFLINE → ${describeStream(stream)}`);
  }, [stream]);

  return { stream, loading, tags };
}

// ══════════════════════════════════════════════════════════════════
//  CONNECTION HEALTH
// ══════════════════════════════════════════════════════════════════

export interface ConnectionHealth {
  connectedCount: number;
  status: 'good' | 'degraded' | 'poor';
  total: number;
}

export function useConnectionHealth(): ConnectionHealth {
  const [state, setState] = useState<ConnectionHealth>({
    connectedCount: 0, status: 'good', total: STREAM_RELAYS.length,
  });

  useEffect(() => {
    const check = () => {
      let connected = 0;
      STREAM_RELAYS.forEach(url => {
        const ws = streamRelayPool.get(url);
        if (ws && ws.readyState === WebSocket.OPEN) connected++;
      });

      setState({
        connectedCount: connected,
        status: connected >= 3 ? 'good' : connected >= 1 ? 'degraded' : 'poor',
        total: STREAM_RELAYS.length,
      });

      // Auto-recover: reset fail counts and retry anything that dropped.
      if (connected < 3) {
        STREAM_RELAYS.forEach(url => {
          const ws = streamRelayPool.get(url);
          if (!ws || ws.readyState > 1) {
            streamRelayFails.set(url, 0);
            getOrConnectStreamRelay(url);
          }
        });
      }
    };

    check();
    const interval = setInterval(check, 10_000);
    return () => clearInterval(interval);
  }, []);

  return state;
}

// ══════════════════════════════════════════════════════════════════
//  NIP-05 USERNAME RESOLUTION
// ══════════════════════════════════════════════════════════════════

export function useResolvePubkey(username: string | undefined) {
  const [pubkey, setPubkey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: clear the previous creator's pubkey the moment the username
    // changes. Without this the hook keeps returning the OLD pubkey while the
    // new lookup is in flight (or forever, if it fails), so navigating from
    // /live/alice to /live/bob kept playing Alice — then snapped to Bob when
    // her resolution finally landed. That read on screen as the player
    // randomly switching between channels.
    setPubkey(null);

    if (!username) { setLoading(false); return; }

    setLoading(true);
    let cancelled = false;

    (async () => {
      slog('resolve', `looking up NIP-05 for "${username}"`);
      try {
        const r = await fetch(
          `/.well-known/nostr.json?name=${encodeURIComponent(username)}`,
          { signal: AbortSignal.timeout(8000) },
        );
        if (r.ok) {
          const d = await r.json() as { names?: Record<string, string> };
          const pk = d.names?.[username.toLowerCase()];
          if (pk) slog('resolve', `"${username}" → ${pk.slice(0, 12)}…`);
          else swarn('resolve', `"${username}" not found in /.well-known/nostr.json — no such user`);
          if (!cancelled && pk) setPubkey(pk);
        } else {
          swarn('resolve', `NIP-05 lookup failed with HTTP ${r.status}`);
        }
      } catch (err) {
        swarn('resolve', 'NIP-05 lookup threw', err);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [username]);

  return { pubkey, loading };
}
